import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";
import type { AssessmentGenerationStatusResponse } from "../network/useAssessmentGenerationStatus";

export enum AssessmentFeedbackType {
  CLINICAL_FIDELITY_FEEDBACK = "clinical-fidelity-feedback",
  ORGANIZATION_FEEDBACK = "organization-feedback",
  PMH_ACCURACY_FEEDBACK = "pmh-accuracy-feedback",
}

interface AssessmentFeedbackParams {
  btSpanId: string;
  feedbackType: AssessmentFeedbackType;
}

export const useAssessmentFeedback = ({
  encounterId,
  patientId,
}: {
  encounterId?: string;
  patientId?: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<void, AssessmentFeedbackParams>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Encounter.Assessment.FEEDBACK(
        patientId,
        encounterId,
      ),
      mutationFn: async ({ btSpanId, feedbackType }) => {
        await axiosInstance.post("/charting/ai-charting/assessment/feedback", {
          btSpanId,
          feedbackType,
        });
      },
      onSuccess: (_, { btSpanId }) => {
        const generationStatusQueryKey =
          QueryKeys.Patient.Chart.EpisodesOfCare.Assessment.GENERATION_STATUS(
            patientId,
            encounterId,
          );

        queryClient.setQueryData<AssessmentGenerationStatusResponse>(
          generationStatusQueryKey,
          (currentGenerationStatus) =>
            currentGenerationStatus?.btSpanId === btSpanId
              ? { ...currentGenerationStatus, flagged: true }
              : currentGenerationStatus,
        );
        void queryClient.invalidateQueries({
          queryKey: generationStatusQueryKey,
          exact: true,
          refetchType: "none",
        });
      },
    },
  });
};
